import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PasswordInput from "../components/PasswordInput";
import api, { gfApi } from "../api/client";
import { ClinicAuthContext } from "../auth/ClinicAuthContext";

// Same dark, semi-transparent card layout as pharmacy-frontend's Login.js
// (Login.css: #1d1d1d page, rgba(255,255,255,0.1) card) so staff moving
// between the two portals see one consistent product look — teal is kept
// as the clinic app's accent color (button, focus ring, links) in place
// of pharmacy's navy.
const S = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#1d1d1d",
    padding: "20px 16px",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "rgba(255,255,255,0.1)",
    borderRadius: 20,
    padding: "36px 32px",
    width: "100%",
    maxWidth: 420,
    boxSizing: "border-box",
    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
  },
  title: {
    color: "#dadada",
    marginTop: 0,
    marginBottom: 24,
    fontSize: 16,
    fontWeight: 700,
    textAlign: "center",
  },
  form: {
    width: "100%",
  },
  input: {
    width: "100%",
    border: "1.5px solid transparent",
    borderRadius: 10,
    padding: "11px 14px",
    margin: "8px 0",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    color: "#ffffff",
    background: "rgba(255,255,255,0.12)",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  btn: {
    width: "100%",
    padding: "13px",
    border: "none",
    borderRadius: 10,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    color: "white",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "opacity 0.15s",
    marginTop: 12,
  },
  logoWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 8,
  },
  logoImg: {
    width: "150px",
    height: "auto",
    marginBottom: 20,
    backgroundColor: "black", /* Ensure black background blends well */
    padding: 10,
    borderRadius: "50%",
  },
  errorText: {
    color: "#f85f5f",
    fontSize: 13,
    textAlign: "center",
    margin: "10px 0 0",
  },
  forgotLink: {
    display: "block",
    textAlign: "right",
    marginTop: 6,
    fontSize: 12.5,
    color: "#5fc4e8",
    fontWeight: 600,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 41, 68, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: 16,
  },
  modalCard: {
    background: "white",
    borderRadius: 16,
    padding: "28px 26px",
    width: "100%",
    maxWidth: 380,
    boxSizing: "border-box",
  },
  modalTitle: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: 18,
    fontWeight: 600,
    color: "#0d4f7a",
    margin: "0 0 8px",
  },
  modalBody: { fontSize: 13, color: "#4a6080", margin: "0 0 18px", lineHeight: 1.5 },
  modalLabel: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#4a6080",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  modalInput: {
    width: "100%",
    border: "1.5px solid #e2eaf3",
    borderRadius: 10,
    padding: "11px 14px",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    color: "#0f2944",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  },
  modalActions: { display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" },
  linkBtn: {
    border: "none",
    background: "none",
    color: "#4a6080",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "10px 4px",
  },
  successNote: { fontSize: 13, color: "#166534", margin: "0 0 4px" },
};

export default function ClinicLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, token } = useContext(ClinicAuthContext);
  // RequireAuth passes the page the user was trying to reach (e.g. a link
  // from an SMS), so signing in lands them there rather than on the dashboard.
  const from = location.state?.from;
  const homeFor = (role) => {
    if (role === "doctor") return "/select-clinic";
    return from && /^\/admin(\/|$)/.test(from.pathname) ? `${from.pathname}${from.search || ""}` : "/admin";
  };

  // Already signed in (token kept in localStorage) — skip the form.
  useEffect(() => {
    if (!token) return;
    api.get("my-account/").then((me) => navigate(homeFor(me.data.role), { replace: true })).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [focused, setFocused] = useState("");  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const openForgot = () => {
    setForgotEmail(form.email);
    setForgotDone(false);
    setForgotOpen(true);
  };

  const submitForgot = async () => {
    if (!forgotEmail) return;
    setForgotSending(true);
    try {
      // Always the same generic response regardless of whether the email
      // matched a real account (see clinic.views_auth, anti-enumeration).
      await api.post("password-reset/", { email: forgotEmail });
    } catch {
      // Swallow — same reasoning, don't leak anything from failure shape either.
    } finally {
      setForgotSending(false);
      setForgotDone(true);
    }
  };

  const handleLogin = async () => {
    setError("");
    if (!form.email || !form.password) {
      setError("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      // Doctor username IS their email (see clinic/views_admin.py
      // create_doctor) — one less thing for a doctor to remember.
      // Clinic staff (Employee) log in through this exact same
      // endpoint too — org resolution differs downstream, not here.
      const res = await gfApi.post("token/", {
        username: form.email,
        password: form.password,
      });
      login(res.data.access, res.data.refresh);

      // The token alone doesn't say whether this login is a Doctor or an
      // Employee — clinic/my-account/ (built for the self-service Account
      // page) already returns an explicit role field for exactly this,
      // so there's no need to infer it from another endpoint's error shape
      // (the old approach here reused my-clinics/'s 403-vs-200 as a signal,
      // which meant a genuine failure — network/500 — was indistinguishable
      // from "this is a doctor" and silently misrouted).
      try {
        const me = await api.get("my-account/");
        navigate(homeFor(me.data.role));
      } catch (roleErr) {
        setError("Signed in, but couldn't load your account. Please try again.");
        setLoading(false);
        return;
      }
    } catch (err) {
      const detail = err.response?.data?.detail ?? "Invalid email or password.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Lora:wght@600&display=swap"
        rel="stylesheet"
      />

      <div style={S.card}>
        <div style={S.logoWrap}>
          <img src="/logo.png" alt="Goldfish" style={S.logoImg} />
        </div>
        <h2 style={S.title}>Start Fishing.!</h2>

        <div style={S.form}>
          <input
            type="email"
            style={{
              ...S.input,
              borderColor: focused === "email" ? "#1a6fa8" : "transparent",
              boxShadow: focused === "email" ? "0 0 0 3px rgba(26,111,168,0.35)" : "none",
            }}
            value={form.email}
            onChange={set("email")}
            onFocus={() => setFocused("email")}
            onBlur={() => setFocused("")}
            placeholder="Email"
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
          <PasswordInput
            style={{
              ...S.input,
              borderColor: focused === "password" ? "#1a6fa8" : "transparent",
              boxShadow: focused === "password" ? "0 0 0 3px rgba(26,111,168,0.35)" : "none",
            }}
            value={form.password}
            onChange={set("password")}
            onFocus={() => setFocused("password")}
            onBlur={() => setFocused("")}
            placeholder="Password"
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
          <button type="button" style={S.forgotLink} onClick={openForgot}>
            Forgot password?
          </button>
          <p style={S.errorText}>{error || " "}</p>
          <button onClick={handleLogin} disabled={loading} style={{ ...S.btn, opacity: loading ? 0.75 : 1 }}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </div>

      {forgotOpen && (
        <div style={S.modalBackdrop}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={S.modalTitle}>Reset your password</h3>
            {forgotDone ? (
              <>
                <p style={S.successNote}>If an account exists for that email, a reset link has been sent.</p>
                <div style={S.modalActions}>
                  <button style={S.btn} onClick={() => setForgotOpen(false)}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={S.modalBody}>Enter the email address for your doctor or staff account.</p>
                <label style={S.modalLabel}>Email</label>
                <input
                  type="email"
                  style={S.modalInput}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  onKeyDown={(e) => e.key === "Enter" && submitForgot()}
                />
                <div style={S.modalActions}>
                  <button style={S.linkBtn} onClick={() => setForgotOpen(false)}>
                    Cancel
                  </button>
                  <button
                    style={{ ...S.btn, width: "auto", padding: "10px 18px", marginTop: 0 }}
                    disabled={forgotSending || !forgotEmail}
                    onClick={submitForgot}
                  >
                    {forgotSending ? "Sending…" : "Send reset link"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}